import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { Box } from '../Box/Box.js';
import { Icon } from '../Icon/Icon.js';
import { Image as ImageMemoized } from '../Image/Image.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { Typography } from '../Typography/Typography.js';
import { mappedTokens, getHierarchy, containerPropsMapper, typographyPropsMapper, defaultProps } from './Badge.mappers.js';

var _excluded = ["label"],
  _excluded2 = ["labelVariant", "leadingAsset", "trailingAsset", "variant", "zIndex"];
function isImage(asset) {
  return !!(asset && 'src' in asset);
}

/**
 * Badge is used to highlight a small amount of data. They're ideal for getting user's attention, but should be used sparsely and according to existing [frameworks](https://confluence.agodadev.io/display/DES/Badge+Framework).
 */
var Badge = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var label = _ref.label,
    initialProps = _objectWithoutProperties(_ref, _excluded);
  var labelVariant = initialProps.labelVariant,
    leadingAsset = initialProps.leadingAsset,
    trailingAsset = initialProps.trailingAsset,
    variant = initialProps.variant,
    zIndex = initialProps.zIndex,
    rest = _objectWithoutProperties(initialProps, _excluded2);
  var _mappedTokens$variant = mappedTokens[variant],
    background = _mappedTokens$variant.background,
    border = _mappedTokens$variant.border,
    text = _mappedTokens$variant.text;
  var hierarchy = React.useMemo(function () {
    return getHierarchy(variant);
  }, [variant]);
  var leadingAssetImageSize = '12px';
  var iconOnly = labelVariant === 'tooltip';
  var LeadingAssetElement = React.useMemo(function () {
    return leadingAsset && (isImage(leadingAsset) ? /*#__PURE__*/React.createElement(ImageMemoized, {
      src: leadingAsset.src,
      alt: leadingAsset.alt,
      width: leadingAssetImageSize,
      height: leadingAssetImageSize,
      me: iconOnly ? undefined : '4'
    }) : /*#__PURE__*/React.createElement(Icon, {
      id: leadingAsset.id,
      label: leadingAsset.label,
      size: "14",
      color: text,
      me: iconOnly ? undefined : '4'
    }));
  }, [text, leadingAsset, leadingAssetImageSize, iconOnly]);
  if (!text) return null;
  var containerProps = containerPropsMapper(rest, hierarchy, {
    background: background,
    border: border
  }, iconOnly);
  if (iconOnly) {
    return /*#__PURE__*/React.createElement(Box, {
      display: "inline-flex"
    }, /*#__PURE__*/React.createElement(Tooltip, {
      title: label,
      usePortal: true,
      pointerPosition: "above",
      zIndex: zIndex
    }, /*#__PURE__*/React.createElement(Box, _extends({}, containerProps, {
      ref: ref
    }), LeadingAssetElement)));
  }
  return /*#__PURE__*/React.createElement(Box, _extends({}, containerProps, {
    ref: ref
  }), LeadingAssetElement, /*#__PURE__*/React.createElement(Typography, typographyPropsMapper(rest, hierarchy, {
    text: text
  }), label), trailingAsset && /*#__PURE__*/React.createElement(Icon, {
    id: trailingAsset.id,
    size: "12",
    color: text,
    ms: "4"
  }));
});
Badge.defaultProps = defaultProps;
var BadgeMemoized = /*#__PURE__*/React.memo(Badge);
BadgeMemoized.displayName = 'Badge';

export { BadgeMemoized as Badge, Badge as BadgeWithoutMemoized };
//# sourceMappingURL=Badge.js.map
